import { getAclStatus } from "./aclStatus";

// The single presentation layer over the raw grant-status flag table
// (aclStatus.ts): the one place a grant-status code turns into a user-facing
// kind, label key, chip colour, usability and severity. Kind labels live under
// agenticAccessPanel.status.*; the code-keyed statuses.* vocabulary remains
// only for the owner-badge paths inside ACLStatus.vue.
export type AgenticGrantStatusKind =
  | "active"
  | "published"
  | "pending"
  | "agentOffline"
  | "error"
  | "blocked"
  | "revoking"
  | "revoked";

// Shared chip/badge colour language for grant statuses.
export const GRANT_STATUS_GREEN_CLS = "border-green-500/20 bg-green-500/10 text-green-500";
export const GRANT_STATUS_ORANGE_CLS = "border-orange-500/20 bg-orange-500/10 text-orange-500";
export const GRANT_STATUS_WARNING_CLS = "bg-warning/10 text-warning border-warning/20";
export const GRANT_STATUS_DANGER_CLS = "border-destructive/20 bg-destructive/10 text-danger";

export function agenticGrantStatusKind(code: number): AgenticGrantStatusKind {
  const st = getAclStatus(code);
  if (!st) return "pending";
  if (st.blocked) return "blocked";
  if (st.error) return "error";
  if (st.agentOffline) return "agentOffline";
  // Access on its way out: revoke_error (10) already returned as "error".
  if (st.revoking) return "revoking";
  // Published = on the allowlist, awaiting the enforcing device's next fetch.
  // Knocknoc's side is done (so it colours green like granted) but the user
  // isn't through yet, so it labels and counts as pending, not granted.
  if (st.name === "Published") return "published";
  if (st.grantIssued) return "active";
  return "pending";
}

// Whether this status means the agent has (or is about to have) usable access.
export function agenticGrantStatusUsable(code: number): boolean {
  const kind = agenticGrantStatusKind(code);
  return kind === "active" || kind === "published" || kind === "pending";
}

// Chip styling per kind, matching the existing pending (orange) and countdown
// (green) badges on the agentic card.
export function agenticGrantStatusChipClass(kind: AgenticGrantStatusKind): string {
  switch (kind) {
    case "active":
    case "published":
      return GRANT_STATUS_GREEN_CLS;
    case "error":
    case "blocked":
      return GRANT_STATUS_DANGER_CLS;
    // Revoking is warning-tinted (access on its way OUT) so it reads
    // differently from the orange pending (on its way in) and muted revoked.
    case "revoking":
      return GRANT_STATUS_WARNING_CLS;
    case "revoked":
      return "border-border bg-muted text-muted-foreground";
    default:
      return GRANT_STATUS_ORANGE_CLS;
  }
}

// i18n key for a kind's user-facing label. Concatenation (not a template
// literal) so the i18n unused-key scan recognises the dynamic prefix.
export function agenticGrantStatusLabelKey(kind: AgenticGrantStatusKind): string {
  return "agenticAccessPanel.status." + kind;
}

// Mirrors the backend's OrderGrantStatusByDisruptiveness
// (server/backend/internal/repo/grant_status.go) so the frontend names the
// same "worst" failure the server would.
export const GRANT_STATUS_DISRUPTIVENESS = [4, 2, 6, 0, 5, 1, 3, 8, 10, 11];

export function mostDisruptiveCode(codes: number[]): number {
  let worst = codes[0];
  for (const code of codes) {
    if (GRANT_STATUS_DISRUPTIVENESS.indexOf(code) > GRANT_STATUS_DISRUPTIVENESS.indexOf(worst)) worst = code;
  }
  return worst;
}

// Badge colour for the owner's own (non-aggregated) status on the knoc card.
const OWNER_STATUS_CLS: Record<number, string> = {
  0: GRANT_STATUS_GREEN_CLS,
  1: GRANT_STATUS_DANGER_CLS,
  2: GRANT_STATUS_WARNING_CLS,
  3: GRANT_STATUS_DANGER_CLS,
  4: "hidden",
  5: GRANT_STATUS_GREEN_CLS,
  // Published labels as "Pending" (awaiting the device's next fetch) but
  // colours green, knocknoc's side is done.
  6: GRANT_STATUS_GREEN_CLS,
  8: GRANT_STATUS_WARNING_CLS,
  10: GRANT_STATUS_DANGER_CLS,
};

export function ownerGrantStatusBadgeClass(code: number): string {
  return OWNER_STATUS_CLS[code] ?? "";
}
